// ============================================================================
// useUserPreferences Hook
// Feature: 035-app-shell-enhancement
// Author: Peter Dobler
// Created: 2026-01-21
// Description: Hook for accessing and managing user preferences. Provides a
//              convenient interface to the UserPreferencesContext.
// ============================================================================

import { useCallback, useMemo } from 'react';
import { useUserPreferencesContext } from '../contexts/UserPreferencesContext';
import type { ThemeMode, UserPreferences, LandingPage } from '../types/shell';

/**
 * useUserPreferences - Hook for accessing and managing user preferences
 *
 * @remarks
 * This hook provides a streamlined interface to user preferences including
 * theme, sidebar state, favorites, and default landing page settings.
 *
 * @example
 * ```tsx
 * const { theme, setTheme, sidebarCollapsed, toggleSidebar } = useUserPreferences();
 *
 * // Change theme
 * setTheme('dark');
 *
 * // Toggle sidebar
 * toggleSidebar();
 *
 * // Add to favorites
 * addFavorite('/workspaces');
 * ```
 */
export const useUserPreferences = () => {
  const {
    preferences,
    updatePreference,
    setPreferences,
    resetPreferences,
    addFavorite,
    removeFavorite,
    isFavorite,
    isStorageAvailable,
  } = useUserPreferencesContext();

  // ==========================================================================
  // Theme Helpers
  // ==========================================================================

  /**
   * Current theme setting
   */
  const theme = preferences.theme;

  /**
   * Set the theme preference
   */
  const setTheme = useCallback(
    (newTheme: ThemeMode) => {
      updatePreference('theme', newTheme);
    },
    [updatePreference]
  );

  // ==========================================================================
  // Sidebar Helpers
  // ==========================================================================

  /**
   * Current sidebar collapsed state
   */
  const sidebarCollapsed = preferences.sidebarCollapsed;

  /**
   * Set sidebar collapsed state explicitly
   */
  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      updatePreference('sidebarCollapsed', collapsed);
    },
    [updatePreference]
  );

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = useCallback(() => {
    updatePreference('sidebarCollapsed', !preferences.sidebarCollapsed);
  }, [updatePreference, preferences.sidebarCollapsed]);

  // ==========================================================================
  // Default Landing Page Helpers
  // ==========================================================================

  /**
   * Current default landing page
   */
  const defaultLandingPage = preferences.defaultLandingPage;

  /**
   * Set default landing page
   */
  const setDefaultLandingPage = useCallback(
    (path: LandingPage | null) => {
      updatePreference('defaultLandingPage', path);
    },
    [updatePreference]
  );

  // ==========================================================================
  // Favorites Helpers
  // ==========================================================================

  /**
   * Current list of favorite paths
   */
  const favorites = preferences.favorites;

  /**
   * Toggle a path in favorites (add if not present, remove if present)
   */
  const toggleFavorite = useCallback(
    (path: string) => {
      if (isFavorite(path)) {
        removeFavorite(path);
      } else {
        addFavorite(path);
      }
    },
    [isFavorite, addFavorite, removeFavorite]
  );

  // ==========================================================================
  // Bulk Operations
  // ==========================================================================

  /**
   * Update multiple preferences at once
   */
  const updatePreferences = useCallback(
    (updates: Partial<UserPreferences>) => {
      setPreferences(updates);
    },
    [setPreferences]
  );

  /**
   * Reset all preferences to default values
   */
  const resetToDefaults = useCallback(() => {
    resetPreferences();
  }, [resetPreferences]);

  // ==========================================================================
  // Return Value
  // ==========================================================================

  return useMemo(
    () => ({
      // Raw preferences
      preferences,

      // Theme
      theme,
      setTheme,

      // Sidebar
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebar,

      // Default landing page
      defaultLandingPage,
      setDefaultLandingPage,

      // Favorites
      favorites,
      addFavorite,
      removeFavorite,
      isFavorite,
      toggleFavorite,

      // Bulk operations
      updatePreferences,
      resetToDefaults,

      // Storage status
      isStorageAvailable,
    }),
    [
      preferences,
      theme,
      setTheme,
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebar,
      defaultLandingPage,
      setDefaultLandingPage,
      favorites,
      addFavorite,
      removeFavorite,
      isFavorite,
      toggleFavorite,
      updatePreferences,
      resetToDefaults,
      isStorageAvailable,
    ]
  );
};

export default useUserPreferences;
